/**
 * Standard aziendali per-RSA: a differenza della matrice requisiti regionale
 * (righe=profili × colonne=target) qui il dato è 1D — mansione → valore per
 * RSA (si applica all'occupazione totale, vedi domain/fabbisogno.js
 * computeAziendali) — quindi lista CRUD, non griglia 2D.
 */
import React, { useState } from "react";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  Spinner,
  ErrorBox,
  SimpleTable,
} from "../../components/ui/basics";
import RsaSelect from "../../components/RsaSelect";
import MansioneSelect from "../../components/MansioneSelect";
import { TIPO_CALCOLO, UNITA_MISURA } from "../../constants/normativeTipo";

const RSA_DEFAULT = "_DEFAULT";

export default function AziendaliSection() {
  const { apiRequest } = useAuth();
  const [rsa, setRsa] = useState(null);
  const isDefault = rsa === RSA_DEFAULT;
  const endpoint = rsa
    ? `/normative/aziendali?rsa=${encodeURIComponent(rsa)}`
    : null;
  const { data, loading, error, reload } = useApi(endpoint, [rsa]);
  const ereditatiEndpoint =
    rsa && !isDefault
      ? `/normative/aziendali/effettivi?rsa=${encodeURIComponent(rsa)}`
      : null;
  const { data: ereditatiData } = useApi(ereditatiEndpoint, [rsa]);
  const ereditati = (ereditatiData || []).filter(
    (r) => r.origine === "default",
  );
  const [modal, setModal] = useState(null);
  const [copyModal, setCopyModal] = useState(false);

  const submit = async (values) => {
    const payload = {
      ...values,
      valore: values.valore
        ? Number(String(values.valore).replace(",", "."))
        : null,
      rapportobase: values.rapportobase ? Number(values.rapportobase) : null,
      rapportovalore: values.rapportovalore
        ? Number(values.rapportovalore)
        : null,
    };
    if (modal.mode === "create") {
      await apiRequest("/normative/aziendali", {
        method: "POST",
        body: JSON.stringify({ ...payload, rsa }),
      });
    } else {
      await apiRequest(`/normative/aziendali/${modal.record.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    }
    reload();
  };

  const elimina = async (r) => {
    if (
      !window.confirm(`Eliminare lo standard aziendale "${r.mansionecodice}"?`)
    )
      return;
    await apiRequest(`/normative/aziendali/${r.id}`, { method: "DELETE" });
    reload();
  };

  return (
    <Card
      title="Standard aziendali per-RSA"
      subtitle={
        isDefault
          ? "Default aziendale: si applica a tutte le RSA salvo override specifico per singola struttura"
          : "Override/aggiunte specifiche di una RSA rispetto alla normativa regionale (e al default aziendale)"
      }
      actions={
        rsa && (
          <div className="flex gap-2">
            <button
              onClick={() => setCopyModal(true)}
              className="text-sm border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-50"
            >
              📋 Copia da altra RSA
            </button>
            <button
              onClick={() => setModal({ mode: "create" })}
              className="text-sm bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700"
            >
              + Nuovo standard
            </button>
          </div>
        )
      }
    >
      <RsaSelect value={rsa} onChange={setRsa} allowDefault />
      {!rsa && (
        <p className="text-sm text-gray-400 py-8 text-center">
          Seleziona una RSA, oppure il default aziendale.
        </p>
      )}
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {rsa && data && (
        <SimpleTable
          rowKey={(r) => r.id}
          columns={[
            { key: "mansionecodice", label: "Mansione" },
            { key: "tipocalcolo", label: "Tipo" },
            {
              key: "valore",
              label: "Valore",
              align: "right",
              render: (r) =>
                r.tipocalcolo === "RAPPORTO"
                  ? `${r.rapportovalore}/${r.rapportobase} ospiti`
                  : `${r.valore} ${r.unita || ""}`,
            },
            { key: "note", label: "Note" },
            {
              key: "azioni",
              label: "",
              render: (r) => (
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setModal({ mode: "edit", record: r })}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => elimina(r)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Elimina
                  </button>
                </div>
              ),
            },
          ]}
          rows={data}
        />
      )}

      {rsa && !isDefault && ereditati.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-500 mb-2">
            Ereditati dal default aziendale (nessun override specifico su {rsa}
            ):
          </p>
          <SimpleTable
            rowKey={(r) => r.mansionecodice}
            columns={[
              { key: "mansionecodice", label: "Mansione" },
              { key: "tipocalcolo", label: "Tipo" },
              {
                key: "valore",
                label: "Valore",
                align: "right",
                render: (r) =>
                  r.tipocalcolo === "RAPPORTO"
                    ? `${r.rapportovalore}/${r.rapportobase} ospiti`
                    : `${r.valore} ${r.unita || ""}`,
              },
              { key: "note", label: "Note" },
              {
                key: "azioni",
                label: "",
                render: (r) => (
                  <button
                    onClick={() =>
                      setModal({
                        mode: "create",
                        record: {
                          mansionecodice: r.mansionecodice,
                          tipocalcolo: r.tipocalcolo,
                          valore: r.valore,
                          unita: r.unita,
                          rapportobase: r.rapportobase,
                          rapportovalore: r.rapportovalore,
                        },
                      })
                    }
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Sovrascrivi qui
                  </button>
                ),
              },
            ]}
            rows={ereditati}
          />
        </div>
      )}

      {modal && (
        <AziendaleModal
          mode={modal.mode}
          record={modal.record}
          onSubmit={submit}
          onClose={() => setModal(null)}
        />
      )}
      {copyModal && (
        <CopyAziendaliModal
          toRsa={rsa}
          onClose={() => setCopyModal(false)}
          onCopied={reload}
        />
      )}
    </Card>
  );
}

function AziendaleModal({ mode, record, onSubmit, onClose }) {
  const [values, setValues] = useState(
    record || { tipocalcolo: "MINUTAGGIO", unita: "MIN_DIE" },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isRapporto = values.tipocalcolo === "RAPPORTO";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            {mode === "create"
              ? "Nuovo standard aziendale"
              : "Modifica standard aziendale"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Mansione *</span>
            <MansioneSelect
              value={values.mansionecodice}
              onChange={(v) => setValues((s) => ({ ...s, mansionecodice: v }))}
              required
              className="border border-gray-300 rounded px-2 py-1.5 w-full"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">
              Tipo di calcolo *
            </span>
            <select
              value={values.tipocalcolo}
              onChange={(e) =>
                setValues((s) => ({ ...s, tipocalcolo: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1.5 w-full"
            >
              {TIPO_CALCOLO.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {isRapporto ? (
            <div className="flex gap-2">
              <label className="block text-sm flex-1">
                <span className="block text-xs text-gray-500 mb-1">
                  Ogni N ospiti *
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  required
                  value={values.rapportobase ?? ""}
                  onChange={(e) =>
                    setValues((s) => ({ ...s, rapportobase: e.target.value }))
                  }
                  className="border border-gray-300 rounded px-2 py-1.5 w-full"
                />
              </label>
              <label className="block text-sm flex-1">
                <span className="block text-xs text-gray-500 mb-1">
                  Addetti richiesti *
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  required
                  value={values.rapportovalore ?? ""}
                  onChange={(e) =>
                    setValues((s) => ({ ...s, rapportovalore: e.target.value }))
                  }
                  className="border border-gray-300 rounded px-2 py-1.5 w-full"
                />
              </label>
            </div>
          ) : (
            <div className="flex gap-2">
              <label className="block text-sm flex-1">
                <span className="block text-xs text-gray-500 mb-1">
                  Valore *
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  required
                  value={values.valore ?? ""}
                  onChange={(e) =>
                    setValues((s) => ({ ...s, valore: e.target.value }))
                  }
                  className="border border-gray-300 rounded px-2 py-1.5 w-full"
                />
              </label>
              <label className="block text-sm flex-1">
                <span className="block text-xs text-gray-500 mb-1">Unità</span>
                <select
                  value={values.unita || "MIN_DIE"}
                  onChange={(e) =>
                    setValues((s) => ({ ...s, unita: e.target.value }))
                  }
                  className="border border-gray-300 rounded px-2 py-1.5 w-full"
                >
                  {UNITA_MISURA.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Note</span>
            <input
              type="text"
              value={values.note ?? ""}
              onChange={(e) =>
                setValues((s) => ({ ...s, note: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1.5 w-full"
            />
          </label>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CopyAziendaliModal({ toRsa, onClose, onCopied }) {
  const { apiRequest } = useAuth();
  const [fromRsa, setFromRsa] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const r = await apiRequest("/normative/aziendali/copy", {
        method: "POST",
        body: JSON.stringify({ fromRsa, toRsa, overwrite }),
      });
      setResult(r);
      onCopied();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">
            Copia standard aziendali
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <label className="block text-sm">
            <span className="block text-xs text-gray-500 mb-1">Da RSA</span>
            <RsaSelect value={fromRsa} onChange={setFromRsa} allowDefault />
          </label>
          <p className="text-xs text-gray-500">
            A: {toRsa === "_DEFAULT" ? "🏢 Default aziendale" : toRsa}
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
            />
            Sovrascrivi standard esistenti su{" "}
            {toRsa === "_DEFAULT" ? "il default aziendale" : toRsa}
          </label>
          {result && (
            <p className="text-xs text-green-700">
              Copiati {result.copied}, saltati {result.skipped}, eliminati{" "}
              {result.deleted}
            </p>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Chiudi
            </button>
            <button
              type="submit"
              disabled={saving || !fromRsa}
              className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Copia in corso…" : "Copia"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
